import * as React from 'react'
import { cn } from '../../utils/cn'
import { school } from '../../tokens'
import crestAsset from '../../assets/crest.png'

/**
 * JPG Academy Crest
 *
 * The school crest rendered at various sizes.
 * Always maintains its aspect ratio and circular visual weight.
 *
 * The crest is the most authoritative visual element in the design system —
 * use it sparingly and with intention:
 *   - App headers / sidebar branding (small, 32–40px)
 *   - Login / splash screens (large, 64–120px)
 *   - ID cards, certificates, official documents
 *   - Print materials
 *
 * Never use the crest:
 *   - As a decorative pattern or watermark
 *   - At sizes below 24px (loses legibility)
 *   - More than once per screen (except navigation persistence)
 *
 * Usage:
 *   <Crest size="sm" />
 *   <Crest size="lg" className="mx-auto" />
 *   <Crest size="xl" decorative />
 */

type CrestSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const sizeStyles: Record<CrestSize, string> = {
	xs: 'w-6 h-6',
	sm: 'w-8 h-8',
	md: 'w-10 h-10',
	lg: 'w-14 h-14',
	xl: 'w-20 h-20',
}

const decorativeSizeStyles: Record<CrestSize, string> = {
	xs: 'w-6 h-6',
	sm: 'w-10 h-10',
	md: 'w-16 h-16',
	lg: 'w-24 h-24',
	xl: 'w-32 h-32',
}

export interface CrestProps extends React.ImgHTMLAttributes<HTMLImageElement> {
	/** Preset size */
	size?: CrestSize
	/** Use the larger "decorative" sizing (for hero/splash use) */
	decorative?: boolean
	/** Show a circular clipped variant */
	circular?: boolean
	/** Override the crest image source */
	src?: string
}

export const Crest = React.forwardRef<HTMLImageElement, CrestProps>(
	(
		{
			size = 'md',
			decorative = false,
			circular = false,
			src,
			className,
			alt = `${school.shortName} crest`,
			...props
		},
		ref,
	) => {
		return (
			<img
				ref={ref}
				src={src || crestAsset}
				alt={alt}
				className={cn(
					'object-contain',
					decorative ? decorativeSizeStyles[size] : sizeStyles[size],
					// Decorative mode adds a subtle gold glow
					decorative && 'drop-shadow-[0_0_12px_rgba(196,151,42,0.2)]',
					// Circular clip
					circular && 'rounded-full',
					className,
				)}
				draggable={false}
				{...props}
			/>
		)
	},
)

Crest.displayName = 'Crest'
